import { getSiteRedirectEnterPageScene } from '@/hooks/useSiteRedirect';
import { getSession, isInZeloolApp, removeSession, setSession } from '@/utils/tools';

export type EntrancePageScene =
  | '国家站跳转'
  | '直接进入'
  | '首页banner'
  | '顶部导航'
  | '列表页广告位'
  | '搜索'
  | '顶通'
  | '个人中心'
  | '侧边导航'
  | '其他';

export type CampaignEntranceScene =
  | Exclude<EntrancePageScene, '国家站跳转' | '直接进入' | '首页banner' | '其他'>
  | '首页banner';

const CAMPAIGN_ENTRANCE_SCENE_KEY = 'zl_campaignEntrancePageScene';

const campaignEntranceScenes = new Set<CampaignEntranceScene>([
  '首页banner',
  '顶部导航',
  '列表页广告位',
  '搜索',
  '顶通',
  '个人中心',
  '侧边导航',
]);

const appEntranceScenes = new Set<EntrancePageScene>([
  '国家站跳转',
  '直接进入',
  '首页banner',
  '顶部导航',
  '列表页广告位',
  '搜索',
  '顶通',
  '个人中心',
  '侧边导航',
  '其他',
]);

interface EntrancePageSceneInput {
  referPageName?: string;
  referUrlPath?: string;
}

export function isCampaignPageUrl(url?: string | number | null) {
  if (!url) return false;

  try {
    const pathname = new URL(String(url), 'https://www.zeelool.com').pathname;
    return pathname === '/campaigns' || pathname.startsWith('/campaigns/');
  } catch {
    return false;
  }
}

export function setCampaignEntrancePageScene(
  scene: CampaignEntranceScene,
  targetUrl?: string | number | null,
) {
  if (isCampaignPageUrl(targetUrl)) {
    setSession(CAMPAIGN_ENTRANCE_SCENE_KEY, scene);
  }
}

function consumeCampaignEntrancePageScene(): CampaignEntranceScene | '' {
  const scene = getSession(CAMPAIGN_ENTRANCE_SCENE_KEY);
  removeSession(CAMPAIGN_ENTRANCE_SCENE_KEY);

  return typeof scene === 'string' && campaignEntranceScenes.has(scene as CampaignEntranceScene)
    ? (scene as CampaignEntranceScene)
    : '';
}

function getAppEntrancePageSceneFromUrl(): EntrancePageScene | '' {
  if (typeof window === 'undefined' || !isInZeloolApp()) return '';

  try {
    const scene = new URLSearchParams(window.location.search).get('entrance_page_scene');
    return scene && appEntranceScenes.has(scene as EntrancePageScene)
      ? (scene as EntrancePageScene)
      : '';
  } catch {
    return '';
  }
}

export function getEntrancePageScene({
  referPageName = '',
  referUrlPath = '',
}: EntrancePageSceneInput): EntrancePageScene {
  const campaignEntranceScene = consumeCampaignEntrancePageScene();
  const appEntrancePageScene = getAppEntrancePageSceneFromUrl();

  if (appEntrancePageScene) return appEntrancePageScene;

  if (getSiteRedirectEnterPageScene()) return '国家站跳转';

  if (campaignEntranceScene) return campaignEntranceScene;

  if (!referPageName || !referUrlPath) return '直接进入';

  const normalizedPageName = referPageName.trim().toLowerCase();
  if (
    normalizedPageName === 'home' ||
    normalizedPageName.includes('首页') ||
    /^https?:\/\/[^/]+\/?(?:[?#].*)?$/.test(referUrlPath) ||
    referUrlPath === '/'
  ) {
    return '首页banner';
  }

  return '其他';
}
